import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, API_BASE, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import SignatureCanvas from "@/components/SignatureCanvas";
import {
  ArrowLeft, Save, Loader2, Trash2, Plus, Upload, Camera, Link2, Copy, Download,
  User as UserIcon, Building2, Lock, Unlock,
} from "lucide-react";

async function fileToResizedDataUrl(file, maxDim = 1400, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const r = width > height ? maxDim / width : maxDim / height;
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

const newEquipment = () => ({ id: crypto.randomUUID(), description: "", unit: "PZA", quantity: 1, model: "", serial: "" });

export default function ReportEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isNew = !id || id === "new";
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [publicUrl, setPublicUrl] = useState("");
  const [rep, setRep] = useState({
    title: "", site: "",
    service_date: new Date().toISOString().slice(0, 10),
    client: { client_id: null, name: "", rfc: "", email: "", phone: "", address: "", contact_name: "" },
    equipment: [newEquipment()],
    photos: [],
    intro: "",
    conclusion: "",
    technician_signature: null,
    technician_signature_name: user?.name || "",
    client_signature: null,
    client_signature_name: "",
    status: "borrador",
    folio: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const cRes = await api.get("/clients");
        setClients(cRes.data || []);
        if (!isNew) {
          const r = await api.get(`/reports/${id}`);
          setRep({
            ...r.data,
            service_date: r.data.service_date ? r.data.service_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
            technician_signature_name: r.data.technician_signature_name || user?.name || "",
          });
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [id]);

  const pickClient = (cid) => {
    if (cid === "__none__") return;
    const c = clients.find((x) => x.id === cid);
    if (c) setRep((p) => ({
      ...p,
      client: { client_id: c.id, name: c.name, rfc: c.rfc || "", email: c.email || "", phone: c.phone || "", address: c.address || "", contact_name: c.contact_name || "" },
    }));
  };

  const updateEq = (idx, patch) => setRep((p) => {
    const eq = [...p.equipment]; eq[idx] = { ...eq[idx], ...patch }; return { ...p, equipment: eq };
  });
  const addEq = () => setRep((p) => ({ ...p, equipment: [...p.equipment, newEquipment()] }));
  const rmEq = (idx) => setRep((p) => ({ ...p, equipment: p.equipment.filter((_, i) => i !== idx) }));

  const uploadPhotos = async (files) => {
    const arr = Array.from(files || []).slice(0, 20);
    const added = [];
    for (const f of arr) {
      if (!f.type.startsWith("image/")) continue;
      try {
        const dataUrl = await fileToResizedDataUrl(f);
        added.push({
          id: crypto.randomUUID(),
          order: (rep.photos?.length || 0) + added.length,
          data_url: dataUrl,
          caption: "",
        });
      } catch {}
    }
    setRep((p) => ({ ...p, photos: [...(p.photos || []), ...added] }));
    toast.success(`${added.length} foto(s) agregada(s)`);
  };
  const setPhotoCaption = (id, caption) => setRep((p) => ({ ...p, photos: p.photos.map((x) => x.id === id ? { ...x, caption } : x) }));
  const rmPhoto = (id) => setRep((p) => ({ ...p, photos: p.photos.filter((x) => x.id !== id) }));

  const save = async (patchExtra) => {
    if (!rep.client.name?.trim()) return toast.error("Falta el nombre del cliente");
    setSaving(true);
    try {
      const payload = {
        title: rep.title, site: rep.site,
        service_date: rep.service_date ? new Date(rep.service_date).toISOString() : null,
        client: rep.client,
        equipment: rep.equipment,
        photos: rep.photos,
        intro: rep.intro, conclusion: rep.conclusion,
        technician_signature: rep.technician_signature,
        technician_signature_name: rep.technician_signature_name,
        ...(patchExtra || {}),
      };
      if (isNew) {
        const r = await api.post("/reports", { title: payload.title, site: payload.site, service_date: payload.service_date, client: payload.client, equipment: payload.equipment, intro: payload.intro, conclusion: payload.conclusion });
        const r2 = await api.patch(`/reports/${r.data.id}`, payload);
        toast.success(`Reporte ${r2.data.folio} creado`);
        nav(`/reports/${r2.data.id}`);
      } else {
        const r = await api.patch(`/reports/${id}`, payload);
        setRep((p) => ({ ...p, ...r.data, service_date: r.data.service_date ? r.data.service_date.slice(0, 10) : p.service_date }));
        toast.success("Cambios guardados");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const genLink = async () => {
    try {
      const r = await api.post(`/reports/${id}/generate-public-link`);
      setPublicUrl(r.data.public_url);
      setShowLinkModal(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const openPDF = () => {
    fetch(`${API_BASE}/reports/${id}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank"))
      .catch(() => toast.error("Error PDF"));
  };

  const closeReport = async () => {
    if (!rep.technician_signature) return toast.error("Falta la firma del técnico antes de cerrar");
    if (!window.confirm("¿Cerrar reporte? Ya no podrás editar salvo como admin.")) return;
    await save({ status: "cerrado" });
  };

  const reopenReport = async () => {
    if (user?.role !== "admin") return;
    await save({ status: "borrador" });
  };

  if (loading) return <div className="p-8 text-slate-500">Cargando…</div>;

  const locked = rep.status === "cerrado" && user?.role !== "admin";

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto space-y-6 pb-24" data-testid="report-editor">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/reports" className="text-xs text-[#2E75B5] hover:underline flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Volver a reportes
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl lg:text-3xl font-black text-[#0A1118] tracking-tight">
              {isNew ? "Nuevo reporte" : `${rep.folio || ""}`}
            </h1>
            {!isNew && (
              <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded border ${rep.status === "cerrado" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                {rep.status === "cerrado" ? "Cerrado" : "Borrador"}
              </span>
            )}
          </div>
        </div>
        {!isNew && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={openPDF} data-testid="report-pdf-btn" className="h-10 border-[#005A91]/20"><Download className="h-4 w-4 mr-1.5" /> PDF</Button>
            <Button onClick={genLink} data-testid="report-public-link-btn" className="h-10 bg-[#4ECCC8] hover:bg-[#40b8b4] text-[#005A91] font-bold"><Link2 className="h-4 w-4 mr-1.5" /> Enlace firma</Button>
            {rep.status === "borrador" ? (
              <Button onClick={closeReport} data-testid="report-close-btn" className="h-10 bg-emerald-600 hover:bg-emerald-700 font-bold"><Lock className="h-4 w-4 mr-1.5" /> Cerrar reporte</Button>
            ) : user?.role === "admin" && (
              <Button variant="outline" onClick={reopenReport} data-testid="report-reopen-btn" className="h-10 border-amber-300 text-amber-600"><Unlock className="h-4 w-4 mr-1.5" /> Reabrir</Button>
            )}
          </div>
        )}
      </div>

      <fieldset disabled={locked} className={locked ? "opacity-70 pointer-events-none space-y-6" : "space-y-6"}>

      <div className="pe-card p-6 space-y-3">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Datos generales</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Título / servicio</Label>
            <Input data-testid="rep-title" value={rep.title} onChange={(e) => setRep((p) => ({ ...p, title: e.target.value }))} placeholder="Ej: MTTO PREVENTIVO GENERADOR HUASCA" className="mt-1 h-11 border-[#005A91]/20" />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Fecha de servicio</Label>
            <Input data-testid="rep-date" type="date" value={rep.service_date} onChange={(e) => setRep((p) => ({ ...p, service_date: e.target.value }))} className="mt-1 h-11 border-[#005A91]/20" />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Sitio</Label>
            <Input data-testid="rep-site" value={rep.site || ""} onChange={(e) => setRep((p) => ({ ...p, site: e.target.value }))} placeholder="Ej: SITE HUASCA" className="mt-1 h-11 border-[#005A91]/20" />
          </div>
        </div>
      </div>

      <div className="pe-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Cliente</div>
        </div>
        <div className="mb-3">
          <Select value={rep.client.client_id || "__none__"} onValueChange={pickClient}>
            <SelectTrigger data-testid="rep-client-select" className="h-11 border-[#005A91]/20"><SelectValue placeholder="Elegir cliente existente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Manual (no elegir)</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.rfc ? ` — ${c.rfc}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nombre / Razón</Label>
            <Input data-testid="rep-client-name" value={rep.client.name} onChange={(e) => setRep((p) => ({ ...p, client: { ...p.client, name: e.target.value } }))} className="mt-1 h-11 border-[#005A91]/20" /></div>
          <div><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Contacto</Label>
            <Input data-testid="rep-client-contact" value={rep.client.contact_name || ""} onChange={(e) => setRep((p) => ({ ...p, client: { ...p.client, contact_name: e.target.value } }))} className="mt-1 h-11 border-[#005A91]/20" /></div>
        </div>
      </div>

      <div className="pe-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Equipo / servicio ({rep.equipment.length})</div>
          <Button onClick={addEq} data-testid="add-equipment-btn" className="bg-[#005A91] hover:bg-[#004a7a] h-9"><Plus className="h-4 w-4 mr-1" /> Agregar</Button>
        </div>
        <div className="space-y-2">
          {rep.equipment.map((e, idx) => (
            <div key={e.id} data-testid={`eq-row-${idx}`} className="grid grid-cols-12 gap-2 items-end border border-slate-200 rounded-md p-3">
              <div className="col-span-12 sm:col-span-4"><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Descripción</Label>
                <Input data-testid={`eq-desc-${idx}`} value={e.description} onChange={(ev) => updateEq(idx, { description: ev.target.value })} className="mt-1 h-10 border-[#005A91]/20" /></div>
              <div className="col-span-4 sm:col-span-1"><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Unid.</Label>
                <Input data-testid={`eq-unit-${idx}`} value={e.unit} onChange={(ev) => updateEq(idx, { unit: ev.target.value })} className="mt-1 h-10 border-[#005A91]/20" /></div>
              <div className="col-span-4 sm:col-span-1"><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Cant.</Label>
                <Input data-testid={`eq-qty-${idx}`} type="number" value={e.quantity} onChange={(ev) => updateEq(idx, { quantity: ev.target.value })} className="mt-1 h-10 border-[#005A91]/20 font-numbers" /></div>
              <div className="col-span-4 sm:col-span-3"><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Modelo</Label>
                <Input data-testid={`eq-model-${idx}`} value={e.model} onChange={(ev) => updateEq(idx, { model: ev.target.value })} className="mt-1 h-10 border-[#005A91]/20" /></div>
              <div className="col-span-9 sm:col-span-2"><Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">N° Serie</Label>
                <Input data-testid={`eq-serial-${idx}`} value={e.serial} onChange={(ev) => updateEq(idx, { serial: ev.target.value })} className="mt-1 h-10 border-[#005A91]/20 font-numbers" /></div>
              <div className="col-span-3 sm:col-span-1"><Button variant="ghost" onClick={() => rmEq(idx)} data-testid={`eq-rm-${idx}`} className="h-10 w-full text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button></div>
            </div>
          ))}
        </div>
      </div>

      <div className="pe-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">Fotografías ({rep.photos.length})</div>
          <div>
            <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadPhotos(e.target.files)} data-testid="photo-input" />
            <Button onClick={() => fileInputRef.current?.click()} data-testid="add-photos-btn" className="bg-[#005A91] hover:bg-[#004a7a] h-9"><Camera className="h-4 w-4 mr-1" /> Subir fotos</Button>
          </div>
        </div>
        {rep.photos.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-md">
            Sube fotos desde tu dispositivo. Cada foto puede llevar una descripción.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {rep.photos.map((p, idx) => (
              <div key={p.id} data-testid={`photo-${idx}`} className="border border-slate-200 rounded-md overflow-hidden">
                <img src={p.data_url} alt="foto" className="w-full h-56 object-cover bg-slate-100" />
                <div className="p-3">
                  <Textarea data-testid={`photo-caption-${idx}`} placeholder="Descripción de esta imagen…" rows={2} value={p.caption} onChange={(e) => setPhotoCaption(p.id, e.target.value)} className="border-[#005A91]/20 focus-visible:ring-[#4ECCC8]" />
                  <div className="flex justify-end mt-2">
                    <Button variant="ghost" size="sm" onClick={() => rmPhoto(p.id)} data-testid={`photo-rm-${idx}`} className="h-8 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 mr-1" /> Quitar</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="pe-card p-6 space-y-4">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Descripción del servicio (introducción)</Label>
          <Textarea data-testid="rep-intro" rows={3} value={rep.intro || ""} onChange={(e) => setRep((p) => ({ ...p, intro: e.target.value }))} className="mt-1 border-[#005A91]/20 focus-visible:ring-[#4ECCC8]" placeholder="Ej: Se realizó el mantenimiento preventivo…" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Conclusiones</Label>
          <Textarea data-testid="rep-conclusion" rows={4} value={rep.conclusion || ""} onChange={(e) => setRep((p) => ({ ...p, conclusion: e.target.value }))} className="mt-1 border-[#005A91]/20 focus-visible:ring-[#4ECCC8]" placeholder="Estado final del equipo, recomendaciones…" />
        </div>
      </div>

      <div className="pe-card p-6">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-3">Firma del técnico</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <SignatureCanvas value={rep.technician_signature} onChange={(v) => setRep((p) => ({ ...p, technician_signature: v }))} />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nombre técnico</Label>
            <Input data-testid="rep-tech-name" value={rep.technician_signature_name || ""} onChange={(e) => setRep((p) => ({ ...p, technician_signature_name: e.target.value }))} className="mt-1 h-11 border-[#005A91]/20" />
          </div>
        </div>
      </div>

      {rep.client_signature && (
        <div className="pe-card p-6 bg-emerald-50/40 border-emerald-200">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700 mb-2">Firma del cliente (recibida)</div>
          <img src={rep.client_signature} alt="firma cliente" className="max-h-32 border border-emerald-300 rounded bg-white" />
          <div className="text-sm text-slate-700 mt-2"><b>{rep.client_signature_name}</b> · {rep.accepted_at ? new Date(rep.accepted_at).toLocaleString("es-MX") : ""}</div>
        </div>
      )}

      </fieldset>

      <div className="flex justify-end sticky bottom-4">
        <Button onClick={() => save()} disabled={saving || locked} data-testid="rep-save-btn" className="h-12 bg-[#2E75B5] hover:bg-[#3a88cd] font-bold px-6 shadow-lg">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          {isNew ? "Crear reporte" : "Guardar cambios"}
        </Button>
      </div>

      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enlace de firma para el cliente</DialogTitle>
            <DialogDescription>Compártelo. El cliente firmará su conformidad desde ahí.</DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs break-all font-mono" data-testid="report-public-url">{publicUrl}</div>
          <DialogFooter>
            <Button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copiado"); }} className="bg-[#2E75B5] hover:bg-[#3a88cd]"><Copy className="h-4 w-4 mr-1.5" /> Copiar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
